import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { classes, exactCurrencyFormatter } from "@/utils"
import { Check, ChevronsUpDown, FileText, X } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "preact/hooks"
import {
  AccountingInvoiceJson,
  listAccountingInvoices,
  linkTransactionInvoice,
  unlinkTransactionInvoice,
} from "@/api/accounting.tsx"
import { useOrgSettings } from "@/org_settings/state.tsx"
import { format, parseISO } from "date-fns"

const INTEGRATION_LABELS: Record<string, string> = {
  sageintacct: "Sage Intacct",
  quickbooks: "QuickBooks",
  xero: "Xero",
}

function getIntegrationLabel(integration: string | null): string {
  if (!integration) return "Accounting"
  return INTEGRATION_LABELS[integration.toLowerCase()] || integration
}

interface AccountingInvoiceAutocompleteProps {
  deductionId: string
  onSuccess: () => void
}

export function AccountingInvoiceAutocomplete({
  deductionId,
  onSuccess,
}: AccountingInvoiceAutocompleteProps) {
  const orgSettings = useOrgSettings()
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [allInvoices, setAllInvoices] = useState<AccountingInvoiceJson[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const commandListRef = useRef<HTMLDivElement>(null)

  // Don't render if no accounting integration
  if (!orgSettings.accounting_integration) {
    return null
  }

  const integrationLabel = getIntegrationLabel(orgSettings.accounting_integration)

  // Load all invoices when popover opens for the first time
  useEffect(() => {
    if (open && !hasLoaded) {
      loadInvoices()
    }
  }, [open, hasLoaded])

  async function loadInvoices() {
    setIsLoading(true)
    const result = await listAccountingInvoices()
    if (result.ok) {
      setAllInvoices(result.value.data)
    }
    setIsLoading(false)
    setHasLoaded(true)
  }

  // Find the currently linked invoice for this deduction
  const currentLinkedInvoice = useMemo(() => {
    return allInvoices.find((inv) => inv.assigned_id === deductionId)
  }, [allInvoices, deductionId])

  // Filter invoices based on search query (client-side filtering)
  const filteredInvoices = useMemo(() => {
    if (!searchQuery.trim()) {
      // Show unassigned invoices first, then assigned ones
      return [...allInvoices].sort((a, b) => {
        if (a.assigned_id && !b.assigned_id) return 1
        if (!a.assigned_id && b.assigned_id) return -1
        return 0
      })
    }

    const query = searchQuery.toLowerCase()
    return allInvoices.filter((invoice) => {
      return (
        invoice.invoice_number.toLowerCase().includes(query) ||
        (invoice.invoice_date && invoice.invoice_date.includes(query))
      )
    })
  }, [allInvoices, searchQuery])

  async function handleSelectInvoice(invoice: AccountingInvoiceJson) {
    const result = await linkTransactionInvoice(deductionId, invoice.id)
    if (result.ok) {
      // Update local state to reflect the change
      setAllInvoices((prev) =>
        prev.map((inv) => {
          // Remove assignment from previously linked invoice
          if (inv.assigned_id === deductionId) {
            return { ...inv, assigned_id: undefined }
          }
          // Assign to newly selected invoice
          if (inv.id === invoice.id) {
            return { ...inv, assigned_id: deductionId }
          }
          return inv
        })
      )
      onSuccess()
    }
    setTimeout(() => {
      setOpen(false)
    }, 0)
  }

  async function handleUnlink() {
    const result = await unlinkTransactionInvoice(deductionId)
    if (result.ok) {
      // Update local state to reflect the change
      setAllInvoices((prev) =>
        prev.map((inv) => {
          if (inv.assigned_id === deductionId) {
            return { ...inv, assigned_id: undefined }
          }
          return inv
        })
      )
      onSuccess()
    }
  }

  function handleSearchChange(value: string) {
    setSearchQuery(value)
    // Reset scroll position when search input changes
    if (commandListRef.current) {
      commandListRef.current.scrollTop = 0
    }
  }

  function formatInvoiceDate(dateStr?: string): string {
    if (!dateStr) return ""
    try {
      return format(parseISO(dateStr), "MM/dd/yyyy")
    } catch {
      return dateStr
    }
  }

  return (
    <div class="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`flex-1 justify-start text-left font-normal ${
              currentLinkedInvoice ? "bg-white border-blue-200" : "text-gray-700"
            }`}>
            <div class="flex items-center gap-2 truncate">
              {currentLinkedInvoice ? (
                <>
                  <FileText className="h-4 w-4 shrink-0" />
                  <span class="truncate">{currentLinkedInvoice.invoice_number}</span>
                </>
              ) : (
                <>
                  <ChevronsUpDown className="h-4 w-4 shrink-0" />
                  <span class="text-gray-500">Link {integrationLabel} Invoice</span>
                </>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search ${integrationLabel} invoices...`}
              value={searchQuery}
              onValueChange={handleSearchChange}
            />
            <CommandList ref={commandListRef}>
              {isLoading ? (
                <div class="py-6 text-center text-sm text-gray-500">Loading...</div>
              ) : (
                <>
                  <CommandEmpty>No invoices found.</CommandEmpty>
                  <CommandGroup>
                    {filteredInvoices.map((invoice, index) => {
                      const isCurrentlyLinked = invoice.assigned_id === deductionId
                      const isAssignedElsewhere = invoice.assigned_id && invoice.assigned_id !== deductionId

                      return (
                        <CommandItem
                          key={invoice.id}
                          value={invoice.id}
                          disabled={!!isAssignedElsewhere}
                          className={`${index % 2 === 0 ? "bg-slate-50" : ""} ${
                            isCurrentlyLinked ? "bg-sky-100" : ""
                          } ${isAssignedElsewhere ? "opacity-50" : ""}`}
                          onSelect={() => !isAssignedElsewhere && handleSelectInvoice(invoice)}>
                          <Check
                            className={classes(
                              "mr-2 h-4 w-4 shrink-0",
                              isCurrentlyLinked ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div class="flex flex-col flex-1 min-w-0">
                            <div class="flex items-center justify-between gap-2">
                              <span class="font-medium truncate">{invoice.invoice_number}</span>
                              <span class="text-sm text-gray-600 shrink-0">
                                {exactCurrencyFormatter(invoice.total_amount)}
                              </span>
                            </div>
                            <div class="flex items-center gap-2 text-sm text-gray-500">
                              {invoice.invoice_date && (
                                <span class="shrink-0">{formatInvoiceDate(invoice.invoice_date)}</span>
                              )}
                              {isAssignedElsewhere && <span class="text-orange-600">(assigned)</span>}
                            </div>
                          </div>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {currentLinkedInvoice && (
        <Button variant="ghost" size="icon" class="h-8 w-8 shrink-0" onClick={handleUnlink} title="Unlink invoice">
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
